/**
 * One pending daemon decision and its review: what it asks, who raised it, the payload, and Approve / Reject for the
 * person the server lets review it (`review.canReview`). Shown in the review dialog (opened from the header's "Waiting
 * on you", `DecisionReviewDialog`) and in a conversation's side panel (owner 2026-10-31), where `layout="panel"`
 * draws the buttons as a plain row instead of the dialog's footer.
 */
import { formatDistanceToNow } from 'date-fns';
import { DialogFooter } from '../tailgrids/core/dialog.js';
import { Badge } from '../tailgrids/core/badge.js';
import { Alert, AlertContent, AlertIndicator, AlertTitle } from '../tailgrids/core/alert.js';
import { Skeleton } from '../tailgrids/core/skeleton.js';
import { reviewSchema } from './decision-review-schema.js';
import type { DaemonDecision } from '../../generated/api/types.gen.js';
import { type ReviewAction, useDaemonDecision, useReviewDaemonDecision } from '../../hooks/useDaemonDecision.js';
import { Form, SubmitButton, TextAreaFormField, useZodForm } from '../form/index.js';
import { useToast } from '../ui/Toast.js';
import { DecisionPayload } from './DecisionPayload.js';

const BLOCKED_COPY: Record<NonNullable<NonNullable<DaemonDecision['review']>['blockedReason']>, string> = {
  settled: 'This request has already been decided.',
  expired: 'This request expired before anyone decided it.',
  own_request: 'You raised this request, so someone else has to review it.',
  missing_policy: 'You do not have the permission this decision needs.',
};

function DecisionDetails({ decision }: { decision: DaemonDecision }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <Badge size="sm" color={decision.severity === 'high' || decision.severity === 'critical' ? 'error' : 'gray'}>
          {decision.severity}
        </Badge>
        <span>{decision.actionType.replaceAll('_', ' ')}</span>
        <span aria-hidden="true">·</span>
        <span>
          raised by {decision.raisedBy?.name ?? decision.raisedBy?.kind ?? 'the workforce'}{' '}
          {formatDistanceToNow(new Date(decision.createdAt), { addSuffix: true })}
        </span>
        {decision.expiresAt && (
          <>
            <span aria-hidden="true">·</span>
            <span>expires {formatDistanceToNow(new Date(decision.expiresAt), { addSuffix: true })}</span>
          </>
        )}
      </div>
      {decision.description && <p className="text-sm text-body">{decision.description}</p>}
      <DecisionPayload payload={decision.actionPayload} />
    </div>
  );
}

function ReviewForm({
  decision,
  onDone,
  layout,
}: {
  decision: DaemonDecision;
  onDone: () => void;
  layout: 'dialog' | 'panel';
}) {
  const review = useReviewDaemonDecision(decision.id, decision.review?.via ?? 'daemon_decision');
  const { notify } = useToast();
  const form = useZodForm(reviewSchema, { defaultValues: { action: 'approve', notes: '' } });

  const submit = async ({ action, notes }: { action: ReviewAction; notes?: string }) => {
    await review.mutateAsync({ action, ...(notes ? { notes } : {}) });
    notify(action === 'approve' ? 'Approved' : 'Rejected');
    onDone();
  };

  const buttons = (
    <>
      <SubmitButton variant="danger" appearance="outline" onPress={() => form.setValue('action', 'reject')}>
        Reject
      </SubmitButton>
      <SubmitButton onPress={() => form.setValue('action', 'approve')}>Approve</SubmitButton>
    </>
  );

  return (
    <Form form={form} onSubmit={submit} className="gap-4">
      <TextAreaFormField
        name="notes"
        label="Notes"
        description="Optional when approving. Required when rejecting."
        rows={3}
        placeholder="What should the proposer know?"
      />
      {layout === 'dialog' ? (
        <DialogFooter className="-mx-5 -mb-4 border-t border-border px-5 py-4">{buttons}</DialogFooter>
      ) : (
        <div className="flex justify-end gap-2">{buttons}</div>
      )}
    </Form>
  );
}

export function DecisionReview({
  decisionId,
  onDone,
  layout = 'dialog',
}: {
  decisionId: number;
  onDone: () => void;
  layout?: 'dialog' | 'panel';
}) {
  const { decision, unreadable } = useDaemonDecision(decisionId);
  const blocked = decision?.review?.blockedReason;
  return (
    <div className="space-y-4">
      {unreadable && (
        <Alert status="error">
          <AlertIndicator />
          <AlertContent>
            <AlertTitle>This request could not be loaded. It may have been withdrawn.</AlertTitle>
          </AlertContent>
        </Alert>
      )}
      {!decision && !unreadable && <Skeleton className="h-24 w-full" />}
      {decision && layout === 'panel' && <h2 className="text-lg font-medium text-heading">{decision.title}</h2>}
      {decision && <DecisionDetails decision={decision} />}
      {decision?.review?.canReview && <ReviewForm decision={decision} onDone={onDone} layout={layout} />}
      {decision && !decision.review?.canReview && blocked && (
        <Alert status="info">
          <AlertIndicator />
          <AlertContent>
            <AlertTitle>
              {BLOCKED_COPY[blocked]}
              {blocked === 'missing_policy' && decision.review?.requiredPolicies.length
                ? ` Needed: ${decision.review.requiredPolicies.join(', ')}.`
                : ''}
            </AlertTitle>
          </AlertContent>
        </Alert>
      )}
    </div>
  );
}
